import React from 'react';
import { motion } from 'framer-motion';

const projects = [
  {
    industry: 'Concrete & Trades',
    title: 'Field Estimating to Invoice, in One App',
    description:
      'A mobile-first platform that lets a concrete contractor measure jobs on-site with photo + LiDAR capture, generate branded estimates, and collect payment — with a customer portal and full English/Spanish support.',
    tags: ['Mobile App', 'LiDAR Measurement', 'Invoicing', 'Bilingual'],
  },
  {
    industry: 'Specialty Food Retail',
    title: 'An AI Analyst for the Back Office',
    description:
      'POS data flows in automatically; an AI assistant reads live sales, inventory, waste, and margins, then delivers a plain-English weekly digest of what is working and where money is leaking.',
    tags: ['AI Assistant', 'POS Integration', 'Inventory', 'Email Digests'],
  },
  {
    industry: 'Healthcare / Rehab',
    title: 'Clinical Operations, End to End',
    description:
      'A HIPAA-architected platform for a neuro-rehabilitation provider: referrals and intake, EMR and outcome scoring, therapist scheduling, finance, and patient & family portals — across multiple facilities.',
    tags: ['EMR', 'Scheduling', 'RBAC', 'Patient Portal'],
  },
  {
    industry: 'Interior Design',
    title: 'AI Renders That Respect the Room',
    description:
      'Designers upload a room photo and product references, then generate realistic, spatially accurate renders for client presentations — with side-by-side model comparison and one-click export.',
    tags: ['Generative AI', 'Image Models', 'Design Tooling'],
  },
  {
    industry: 'Urban Pet Care',
    title: 'Booking + Live GPS Walk Tracking',
    description:
      'A multi-role platform for a city pet-care service: customers book walks, admins dispatch and resolve conflicts on a master calendar, and walkers send live GPS-tracked reports with routes, photos, and notes.',
    tags: ['Booking', 'GPS Tracking', 'Multi-Role', 'Scheduling'],
  },
  {
    industry: 'Mortgage Lending',
    title: 'Calculators That Close Deals',
    description:
      'A guided refinance and HELOC calculator suite with region-specific rules, scenario comparison, amortization breakdowns, and clean visualizations that turn site visitors into qualified leads.',
    tags: ['Financial Tools', 'Data Viz', 'Lead Gen'],
  },
];

const Portfolio: React.FC = () => {
  return (
    <section id="work" className="py-20 bg-black relative overflow-hidden scroll-mt-24">
      <motion.div
        className="absolute inset-0 bg-gradient-to-r from-gold-950/20 to-gold-900/10"
        animate={{ backgroundPosition: ['0% center', '100% center', '0% center'] }}
        transition={{ duration: 30, repeat: Infinity, ease: 'linear' }}
        style={{ backgroundSize: '200% auto' }}
      />
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          className="text-center mb-14 max-w-3xl mx-auto"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gold-400 mb-4">Selected Work</h2>
          <p className="text-gray-400">
            Real software, shipped for real businesses across very different industries. A few of the problems we have solved.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {projects.map((project, i) => (
            <motion.div
              key={project.title}
              className="group relative bg-gray-900/70 border border-gold-800/30 rounded-3xl p-8 shadow-xl shadow-gold-900/10 backdrop-blur-sm hover:border-gold-600/50 transition-all duration-300 liquid-glass-card overflow-hidden"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.6, delay: (i % 2) * 0.1 }}
              whileHover={{ y: -6, boxShadow: '0 20px 45px -20px rgba(211, 158, 61, 0.45)' }}
            >
              <span className="inline-block text-xs font-semibold tracking-wider uppercase text-gold-500/90 mb-3">
                {project.industry}
              </span>
              <h3 className="text-xl md:text-2xl font-bold text-gold-300 mb-3 group-hover:text-gold-200 transition-colors">
                {project.title}
              </h3>
              <p className="text-gray-300 text-sm leading-relaxed mb-5">{project.description}</p>
              <div className="flex flex-wrap gap-2">
                {project.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs font-medium text-gold-300/90 bg-gold-400/10 border border-gold-700/30 rounded-full px-3 py-1"
                  >
                    {tag}
                  </span>
                ))}
              </div>
              <div className="pointer-events-none absolute -right-16 -bottom-16 w-48 h-48 bg-gold-500/5 rounded-full blur-3xl group-hover:bg-gold-500/10 transition-all duration-500" />
            </motion.div>
          ))}
        </div>

        <motion.div
          className="text-center mt-14"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <a
            href="/contact"
            className="inline-block px-8 py-3 bg-gradient-to-r from-gold-500 to-gold-600 hover:from-gold-400 hover:to-gold-500 text-black font-semibold rounded-xl shadow-lg shadow-gold-500/30 transition-all duration-300 pulse-glow"
          >
            Have a project like these? Let's talk
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default Portfolio;
